import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { LabelElement } from '@/types/label';
import type { Json } from '@/integrations/supabase/types';

import { AdminLayout, AdminSection } from '@/components/admin/AdminLayout';
import { AdminDashboard } from '@/components/admin/AdminDashboard';
import { UserManagement, UserWithDetails } from '@/components/admin/UserManagement';
import { GroupManagement } from '@/components/admin/GroupManagement';
import { TemplateManagement } from '@/components/admin/TemplateManagement';
import { SecuritySettings } from '@/components/admin/SecuritySettings';
import { MonitoringDashboard } from '@/components/admin/MonitoringDashboard';
import { KioskSettings } from '@/components/admin/KioskSettings';
import { InviteLinks } from '@/components/admin/InviteLinks';
import { LiveActivityFeed } from '@/components/admin/LiveActivityFeed';
import { DatasetManagement } from '@/components/admin/DatasetManagement';

type PermissionType = 
  | 'access_editor' | 'view_templates' | 'edit_templates' | 'delete_templates'
  | 'manage_own_templates' | 'use_kiosk' | 'view_data_panel' | 'use_json_editor'
  | 'batch_import' | 'print_labels' | 'export_pdf' | 'use_data_carousel'
  | 'change_label_size' | 'use_presets' | 'change_settings' | 'use_clipboard'
  | 'manage_elements' | 'use_alignment' | 'view_preview' | 'export_json'
  | 'import_templates' | 'use_grid' | 'use_layers_panel' | 'edit_properties'
  | 'view_changelog' | 'use_zoom' | 'manage_categories' | 'use_field_undo'
  | 'duplicate_templates' | 'submit_templates'
  | 'view_dashboard' | 'use_keyboard_shortcuts' | 'view_help'
  | 'share_templates' | 'lock_templates' | 'version_templates'
  | 'use_snap_to_grid' | 'use_rulers' | 'use_multi_select' | 'resize_elements'
  | 'configure_print_settings' | 'bulk_print' | 'reprint_labels'
  | 'use_csv_import' | 'edit_field_data' | 'use_barcode_scanner' | 'use_mobile_scan'
  | 'view_shared_templates' | 'comment_templates'
  | 'view_own_activity' | 'view_print_history';

const ALL_PERMS: PermissionType[] = [
  'access_editor','view_templates','edit_templates','delete_templates','manage_own_templates',
  'use_kiosk','view_data_panel','use_json_editor','batch_import','print_labels','export_pdf',
  'use_data_carousel','change_label_size','use_presets','change_settings','use_clipboard',
  'manage_elements','use_alignment','view_preview','export_json','import_templates','use_grid',
  'use_layers_panel','edit_properties','view_changelog','use_zoom','manage_categories',
  'use_field_undo','duplicate_templates','submit_templates',
  'view_dashboard','use_keyboard_shortcuts','view_help',
  'share_templates','lock_templates','version_templates',
  'use_snap_to_grid','use_rulers','use_multi_select','resize_elements',
  'configure_print_settings','bulk_print','reprint_labels',
  'use_csv_import','edit_field_data','use_barcode_scanner','use_mobile_scan',
  'view_shared_templates','comment_templates',
  'view_own_activity','view_print_history',
];

interface Group {
  id: string; name: string; description: string | null; permissions: PermissionType[];
}

interface TemplateItem {
  id: string; name: string; isGlobal: boolean; userId: string; username?: string;
  width: number; height: number; elementCount: number; updatedAt: string;
}

interface PresetItem {
  id: string; name: string; description: string; icon: string;
  elements: LabelElement[]; isActive: boolean; sortOrder: number;
  linkUrl: string | null; urlFields: string[] | null;
}

const AdminPage: React.FC = () => {
  const { isAdmin, loading: authLoading } = useAuth();
  const [section, setSection] = useState<AdminSection>('dashboard');
  const [groups, setGroups] = useState<Group[]>([]);
  const [users, setUsers] = useState<UserWithDetails[]>([]);
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [presets, setPresets] = useState<PresetItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [presetsLoading, setPresetsLoading] = useState(false);

  const fetchGroups = async () => {
    const { data: groupsData } = await supabase.from('groups').select('*').order('name');
    if (groupsData) {
      const result: Group[] = [];
      for (const group of groupsData) {
        const { data: perms } = await supabase.from('group_permissions').select('permission').eq('group_id', group.id);
        result.push({ id: group.id, name: group.name, description: group.description, permissions: (perms || []).map(p => p.permission as PermissionType) });
      }
      setGroups(result);
    }
  };

  const fetchUsers = async () => {
    const { data: profiles } = await supabase.from('profiles').select('*').order('username');
    if (!profiles) return;
    const result: UserWithDetails[] = [];
    for (const profile of profiles) {
      const { data: roleData } = await supabase.from('user_roles').select('role').eq('user_id', profile.user_id).eq('role', 'admin').maybeSingle();
      const { data: ugData } = await supabase.from('user_groups').select('group_id, groups(id, name)').eq('user_id', profile.user_id);
      const userGroups = (ugData || []).filter(ug => ug.groups).map(ug => ({ id: (ug.groups as any).id, name: (ug.groups as any).name }));
      let effectivePermissions: PermissionType[] = [];
      if (roleData) {
        effectivePermissions = [...ALL_PERMS];
      } else {
        const groupIds = userGroups.map(g => g.id);
        if (groupIds.length > 0) {
          const { data: gp } = await supabase.from('group_permissions').select('permission').in('group_id', groupIds);
          effectivePermissions = [...new Set((gp || []).map(p => p.permission as PermissionType))];
        }
      }
      result.push({ id: profile.user_id, username: profile.username, isAdmin: !!roleData, isApproved: profile.is_approved, mustChangePassword: profile.must_change_password, groups: userGroups, effectivePermissions });
    }
    setUsers(result);
  };

  const fetchTemplates = async () => {
    setTemplatesLoading(true);
    const { data } = await supabase.from('templates').select('*').order('updated_at', { ascending: false });
    if (data) {
      const userIds = [...new Set(data.map(t => t.user_id))];
      const { data: profiles } = await supabase.from('profiles').select('user_id, username').in('user_id', userIds);
      const map = new Map((profiles || []).map(p => [p.user_id, p.username]));
      setTemplates(data.map(t => ({ id: t.id, name: t.name, isGlobal: t.is_global, userId: t.user_id, username: map.get(t.user_id) || 'Unknown', width: Number(t.width), height: Number(t.height), elementCount: Array.isArray(t.elements) ? t.elements.length : 0, updatedAt: t.updated_at })));
    }
    setTemplatesLoading(false);
  };

  const fetchPresets = async () => {
    setPresetsLoading(true);
    const { data } = await supabase.from('template_presets').select('*').order('sort_order');
    if (data) {
      setPresets(data.map(p => ({ id: p.id, name: p.name, description: p.description, icon: p.icon, elements: p.elements as unknown as LabelElement[], isActive: p.is_active, sortOrder: p.sort_order, linkUrl: (p as any).link_url ?? null, urlFields: (p as any).url_fields ?? null })));
    }
    setPresetsLoading(false);
  };

  useEffect(() => {
    if (isAdmin) {
      Promise.all([fetchGroups(), fetchUsers(), fetchTemplates(), fetchPresets()]).then(() => setLoading(false));
    }
  }, [isAdmin]);

  if (authLoading || loading) {
    return <div className="min-h-screen flex items-center justify-center bg-workspace"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  if (!isAdmin) return null;

  const stats = {
    totalUsers: users.length,
    admins: users.filter(u => u.isAdmin).length,
    activeUsers: users.filter(u => u.isApproved).length,
    pendingUsers: users.filter(u => !u.isApproved).length,
    totalGroups: groups.length,
    totalTemplates: templates.length,
    globalTemplates: templates.filter(t => t.isGlobal).length,
  };

  return (
    <AdminLayout activeSection={section} onSectionChange={setSection} userCount={users.length} groupCount={groups.length}>
      {section === 'dashboard' && <AdminDashboard stats={stats} onNavigate={(s) => setSection(s as AdminSection)} />}
      {section === 'users' && <UserManagement users={users} groups={groups} onRefreshUsers={fetchUsers} onRefreshGroups={fetchGroups} />}
      {section === 'groups' && <GroupManagement groups={groups} users={users} onRefreshGroups={fetchGroups} onRefreshUsers={fetchUsers} />}
      {section === 'templates' && <TemplateManagement templates={templates} presets={presets} templatesLoading={templatesLoading} presetsLoading={presetsLoading} onRefreshTemplates={fetchTemplates} onRefreshPresets={fetchPresets} />}
      {section === 'datasets' && <DatasetManagement />}
      {section === 'onboarding' && <div className="space-y-6"><h2 className="text-2xl font-bold">Onboarding</h2><p className="text-sm text-muted-foreground">Manage invite links and registration settings</p><InviteLinks /></div>}
      {section === 'kiosk' && <div className="space-y-6"><h2 className="text-2xl font-bold">Kiosk Settings</h2><p className="text-sm text-muted-foreground">Configure kiosk mode behavior</p><KioskSettings /></div>}
      {section === 'settings' && <div className="space-y-6"><h2 className="text-2xl font-bold">Settings</h2><p className="text-sm text-muted-foreground">Security and application configuration</p><SecuritySettings /></div>}
      {section === 'monitoring' && <div className="space-y-6"><h2 className="text-2xl font-bold">Monitoring</h2><p className="text-sm text-muted-foreground">System activity and audit logs</p><LiveActivityFeed /><MonitoringDashboard /></div>}
    </AdminLayout>
  );
};

export default AdminPage;
